import React, { useMemo } from 'react';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';

/**
 * Icon identifiers for WallStreetBets slot machine
 */
const ICONS = ['diamond', 'hands', 'moon', 'rocket', 'wsb-og-logo'] as const;

/**
 * Number of times to repeat the icon sequence per column
 * Using 2 repetitions with 50% animation is the most reliable
 * seamless loop pattern (industry standard for infinite scrolls)
 */
const ICON_REPETITIONS = 2;

/**
 * Number of slot machine columns
 */
const COLUMNS = 3;

/**
 * Configuration for each slot machine column
 * Different speeds and delays create visual interest
 */
const COLUMN_CONFIGS = [
  { duration: 8, delay: -1 }, // Column 1: Slowest, smooth
  { duration: 6, delay: -3 }, // Column 2: Fastest
  { duration: 7, delay: -2 },  // Column 3: Medium pace
] as const;

/**
 * Randomizes the order of icons using Fisher-Yates shuffle algorithm
 * Returns an array with the shuffled icons repeated ICON_REPETITIONS times
 *
 * @returns Array of randomized icon identifiers
 */
function randomizeIcons(): string[] {
  // Create a copy of the ICONS array to shuffle
  const sequence = [...ICONS];

  // Fisher-Yates shuffle algorithm
  for (let i = sequence.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [sequence[i], sequence[j]] = [sequence[j], sequence[i]];
  }

  // Repeat the shuffled sequence multiple times for seamless looping
  return Array(ICON_REPETITIONS).fill(sequence).flat();
}

/**
 * WallStreetBets Casino Slot Machine Background Component
 *
 * Creates an animated background with 3 vertical columns that continuously
 * scroll through randomized WSB icons (diamond, hands, moon, rocket, logo).
 *
 * Features:
 * - 3 columns spinning at different speeds (12s, 10s, 8s)
 * - Randomized icon order per column
 * - GPU-accelerated CSS animations
 * - Respects prefers-reduced-motion accessibility setting
 * - Supports light/dark mode
 * - Blur effect while animating for authentic slot machine feel
 *
 * Performance optimizations:
 * - React.memo prevents unnecessary re-renders
 * - useMemo caches randomized sequences
 * - Pure CSS animations (no JavaScript animation loops)
 * - transform: translate3d for GPU acceleration
 */
export const WsbSlotMachineBackground = React.memo(() => {
  // Detect if user prefers reduced motion for accessibility
  const prefersReducedMotion = usePrefersReducedMotion();

  /**
   * Generate randomized icon sequences for each column
   * Memoized to prevent recalculation on re-renders
   */
  const columnSequences = useMemo(
    () => Array.from({ length: COLUMNS }, () => randomizeIcons()),
    []
  );

  return (
    <div
      className="wsb-slot-machine-background"
      aria-hidden="true"
      role="presentation"
    >
      {columnSequences.map((icons, colIndex) => (
        <div
          key={colIndex}
          className="wsb-slot-column"
          style={{
            animationDuration: `${COLUMN_CONFIGS[colIndex].duration}s`,
            animationDelay: `${COLUMN_CONFIGS[colIndex].delay}s`,
            animationPlayState: prefersReducedMotion ? 'paused' : 'running',
          }}
        >
          {icons.map((icon, iconIndex) => (
            <img
              key={`${icon}-${iconIndex}`}
              src={`${process.env.PUBLIC_URL}/assets/wallstreetbets/opti-${icon}.svg`}
              alt=""
              className="wsb-slot-icon"
              draggable={false}
            />
          ))}
        </div>
      ))}
    </div>
  );
});

// Display name for React DevTools
WsbSlotMachineBackground.displayName = 'WsbSlotMachineBackground';
